/**
 * Upload sanitized incident context to the feeds S3 bucket (trazit-tracking #87 Fase 2).
 * Cipher: same AES-GCM as platf_view-feeds. Path is under feeds/incident-context/.
 */

import {
  buildIncidentContextSnapshot,
  persistIncidentContextDraft,
} from './incident-context-capture.js';
import { encryptFeedsCompatibleJson } from './incident-context-feeds-crypto.js';

const CONTEXT_PARAM = 'contextS3Key';

function readJson(key) {
  try {
    return JSON.parse(sessionStorage.getItem(key) || '{}');
  } catch (_) {
    return {};
  }
}

function getAppConfig() {
  return readJson('config');
}

function getFeedsBucketName(config) {
  return config.feedsBucketName || config.bucketName || '';
}

function getS3BaseUrl(config) {
  const bucketName = getFeedsBucketName(config);
  const region = config.region || 'eu-west-3';
  if (config.cloudFrontUrl) return String(config.cloudFrontUrl).replace(/\/$/, '');
  if (!bucketName) return '';
  if (bucketName.includes('.')) {
    return `https://s3.${region}.amazonaws.com/${bucketName}`;
  }
  return `https://${bucketName}.s3.${region}.amazonaws.com`;
}

function safePathSegment(value) {
  return String(value || 'unknown')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .slice(0, 80);
}

function buildObjectKey(config) {
  const session = readJson('userSession');
  const user = safePathSegment(session.userName || session.user || 'anon');
  const db = safePathSegment(config.dbName || session.dbName || 'db');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const rand = Math.random().toString(36).slice(2, 8);
  return `feeds/incident-context/${db}/${user}/${stamp}-${rand}.enc.txt`;
}

/**
 * @returns {Promise<{ contextS3Key: string, url: string } | null>}
 */
export async function uploadIncidentContextToFeedsBucket(options = {}) {
  const config = options.config || getAppConfig();
  const bucketName = getFeedsBucketName(config);
  const baseUrl = getS3BaseUrl(config);
  if (!bucketName || !baseUrl || config.local === true) {
    console.warn('[TRAZiT incident-context] skip S3 upload: no feeds bucket or local mode');
    return null;
  }

  const snapshot = options.snapshot || buildIncidentContextSnapshot({ config });
  persistIncidentContextDraft(snapshot);
  const payload = {
    kind: 'trazit-incident-context',
    schemaVersion: snapshot.schemaVersion || '1.0.0',
    uploadedAt: new Date().toISOString(),
    snapshot,
  };
  const encrypted = await encryptFeedsCompatibleJson(payload);
  const objectKey = buildObjectKey(config);
  const url = `${baseUrl}/${objectKey}`;

  const response = await fetch(url, {
    method: 'PUT',
    body: encrypted,
    headers: {
      'Content-Type': 'text/plain',
      'Cache-Control': 'no-cache',
    },
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`incident-context S3 PUT ${response.status} ${errText.slice(0, 200)}`);
  }
  return { contextS3Key: objectKey, url };
}

export function appendContextS3KeyToUrl(urlStr, contextS3Key) {
  if (!urlStr || !contextS3Key) return urlStr;
  try {
    const u = new URL(urlStr, window.location.origin);
    if (u.searchParams.get(CONTEXT_PARAM)) return urlStr;
    u.searchParams.set(CONTEXT_PARAM, contextS3Key);
    return u.toString();
  } catch (_) {
    const sep = urlStr.includes('?') ? '&' : '?';
    return `${urlStr}${sep}${CONTEXT_PARAM}=${encodeURIComponent(contextS3Key)}`;
  }
}

export function isNewIncidentRequest(urlStr) {
  if (!urlStr) return false;
  try {
    const u = new URL(urlStr, window.location.origin);
    const action = u.searchParams.get('actionName') || '';
    const hitsIncidentApi = /IncidentAPI|ApiIncidents/i.test(urlStr);
    return hitsIncidentApi && action === 'NEW_INCIDENT';
  } catch (_) {
    return /NEW_INCIDENT/i.test(urlStr) && /IncidentAPI|ApiIncidents/i.test(urlStr);
  }
}

function urlAlreadyHasContextKey(urlStr) {
  try {
    return Boolean(new URL(urlStr, window.location.origin).searchParams.get(CONTEXT_PARAM));
  } catch (_) {
    return /contextS3Key=/i.test(urlStr || '');
  }
}

/**
 * GET + decrypt a previously uploaded incident-context object.
 * @param {string} contextS3Key
 * @param {{ config?: object }} [options]
 */
export async function loadIncidentContextFromFeedsBucket(contextS3Key, options = {}) {
  const config = options.config || getAppConfig();
  const baseUrl = getS3BaseUrl(config);
  if (!baseUrl || !contextS3Key) {
    throw new Error('missing feeds bucket or contextS3Key');
  }
  const key = String(contextS3Key).replace(/^\/+/, '');
  const path = key.startsWith('feeds/') ? key : `feeds/${key}`;
  const response = await fetch(`${baseUrl}/${path}`, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`incident-context S3 GET ${response.status}`);
  }
  const text = await response.text();
  const { decryptFeedsCompatibleJson } = await import('./incident-context-feeds-crypto.js');
  return decryptFeedsCompatibleJson(text);
}

export { CONTEXT_PARAM, urlAlreadyHasContextKey };
